import { useMemo, useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { rowColToA1 } from '@/model/address';
import { isGroupable, isMeasurable, prettyField } from '@/data/classify';
import { columnHeader, unitsOf } from '@/data/units';
import { CLASS_LABELS, whyClass } from '@/data/overrides';
import { distinct } from '@/data/aggregate';
import type { AggKind } from '@/data/aggregate';
import type { MeasureClass, SourceKind } from '@/model/types';
import type { ScheduleMeasure, ScheduleSpec } from '@/giraffe/stamp';
import { scheduleExamples } from '@/giraffe/scheduleExamples';
import { FORMATS } from '@/engine/format';
import { SearchSelect } from '@/ui/SearchSelect';

/**
 * Builds a schedule and stamps it as ORDINARY FORMULAS.
 *
 * Nothing is bound afterwards. The cells hold real `GI.*` calls, so they
 * recalculate when the project bakes, show their formula in the formula bar,
 * and can be edited, copied or deleted like any other cell. This pane is a
 * builder, not an owner — press Insert and it is done.
 */

const SOURCES: { id: SourceKind; label: string; hint: string }[] = [
  {
    id: 'areas',
    label: 'Areas',
    hint: 'The Areas sheet — one row per baked section, split further by level. Group by Usage, Building or Level.',
  },
  { id: 'bakedSections', label: 'Baked sections', hint: 'The engine output — areas, unit counts, usage rates.' },
  { id: 'rawSections', label: 'Raw sections', hint: 'What was drawn, before the engine stacked it.' },
  { id: 'usages', label: 'Usages', hint: 'The project usage table — rents, costs, efficiency.' },
];

const AGGS: { id: AggKind; label: string }[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'sum', label: 'Sum' },
  { id: 'avg', label: 'Average' },
  { id: 'weighted', label: 'Weighted average' },
  { id: 'min', label: 'Min' },
  { id: 'max', label: 'Max' },
  { id: 'count', label: 'Count rows' },
  { id: 'countUnique', label: 'Count unique' },
  { id: 'first', label: 'Value' },
];

export function SchedulePane({ onClose }: { onClose: () => void }): JSX.Element {
  useStore();
  const data = store.data();

  const [source, setSource] = useState<SourceKind>('areas');
  const [groupBy, setGroupBy] = useState<string | null>('usage');
  const [pivotBy, setPivotBy] = useState<string | null>(null);
  const [measures, setMeasures] = useState<ScheduleMeasure[]>([]);
  const [usage, setUsage] = useState('');
  const [more, setMore] = useState('');
  const [rows, setRows] = useState(10);
  const [cols, setCols] = useState(8);
  const [showTotal, setShowTotal] = useState(true);

  const fields = data.fields(source);
  const dimensions = useMemo(
    () => fields.filter(isGroupable),
    [fields]
  );
  const measurable = useMemo(
    () => fields.filter((f) => isMeasurable(f, data.classOf(source, f)) || data.classOf(source, f) === 'nested'),
    [fields, data, source]
  );
  const usages = useMemo(
    () => (fields.includes('usage') ? distinct(data.rows(source), 'usage').map(String) : []),
    [fields, data, source]
  );

  // Sensible defaults the moment a source is picked, so Insert always produces
  // something rather than an empty rectangle.
  const effective = measures.length > 0 ? measures : defaultMeasures(measurable);

  const spec: ScheduleSpec = {
    source,
    groupBy,
    pivotBy,
    measures: pivotBy ? effective.slice(0, 1) : effective,
    filters: [usage ? `usage=${usage}` : '', more].filter((f) => f.trim() !== ''),
    rows,
    cols,
    showHeader: true,
    showTotal,
  };

  const insert = (): void => {
    store.stampSchedule({ row: store.sel.row, col: store.sel.col }, spec);
    onClose();
  };

  // The same ready-made schedules the toolbar offers — one definition, so the
  // two places cannot drift into stamping different things under one name.
  const examples = useMemo(() => scheduleExamples(data), [data]);

  return (
    <aside className="gt-pane">
      <header className="gt-pane-head">
        <h2>Insert a schedule</h2>
        <button className="gt-btn" onClick={onClose} title="Close">×</button>
      </header>

      <div className="gt-pane-body">
        <p className="gt-note">
          Stamps ordinary <code>GI.*</code> formulas at the selected cell. They recalculate with
          the project, and every one is a normal cell you can edit, copy or delete.
        </p>

        <button className="gt-btn gt-btn-primary" onClick={insert}>
          Insert at {rowColToA1(store.sel.row, store.sel.col)}
        </button>

        {examples.map((ex) => (
          <button
            key={ex.id}
            className="gt-btn"
            title={ex.title}
            onClick={() => {
              store.stampSchedule({ row: store.sel.row, col: store.sel.col }, ex.spec);
              onClose();
            }}
          >
            {ex.label}
          </button>
        ))}

        <div className="gt-block-editor">
          <div className="gt-field">
            <label htmlFor="sc-src">Source</label>
            <select
              id="sc-src"
              className="gt-select"
              value={source}
              onChange={(e) => {
                setSource(e.target.value as SourceKind);
                setMeasures([]);
                setGroupBy(null);
                setPivotBy(null);
              }}
            >
              {SOURCES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
            <span className="gt-note">{SOURCES.find((s) => s.id === source)?.hint}</span>
          </div>

          <div className="gt-field">
            <label>Rows</label>
            <SearchSelect
              value={groupBy ?? ''}
              onChange={(v) => setGroupBy(v || null)}
              options={[{ value: '', label: 'One row, the whole set' }, ...dimensions.map((f) => ({ value: f, label: prettyField(f) }))]}
            />
          </div>

          <div className="gt-field">
            <label>Columns</label>
            <SearchSelect
              value={pivotBy ?? ''}
              onChange={(v) => setPivotBy(v || null)}
              options={[
                { value: '', label: 'One column per measure' },
                ...dimensions.map((f) => ({ value: f, label: `Cross-tab by ${prettyField(f)}` })),
              ]}
            />
          </div>

          <div className="gt-field">
            <label>{pivotBy ? 'Value' : 'Columns'}</label>
            {(pivotBy ? effective.slice(0, 1) : effective).map((m, i) => {
              const cls = data.classOf(source, m.field);
              const overridden = store.classOverride(source, m.field) !== undefined;
              const inferred = store.inferredClassOf(source, m.field);
              return (
                <div key={i} className="gt-measure-row">
                  {/* The unit in the label, so what lands in the cell is known
                      before it is stamped — the header it writes carries it. */}
                  <SearchSelect
                    value={m.field}
                    onChange={(v) => setMeasures(replace(effective, i, { ...m, field: v, fmt: undefined }))}
                    options={measurable.map((f) => ({
                      value: f,
                      label: `${columnHeader(f, unitsOf(data))}${data.classOf(source, f) === 'nested' ? ' (nested)' : ''}`,
                    }))}
                  />
                  <select
                    className="gt-select"
                    value={m.agg}
                    title={whyClass(m.field, cls, overridden)}
                    onChange={(e) => setMeasures(replace(effective, i, { ...m, agg: e.target.value as AggKind }))}
                  >
                    {AGGS.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.id === 'auto' ? `Auto (${cls})` : a.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="gt-select"
                    value={m.fmt ?? ''}
                    onChange={(e) =>
                      setMeasures(replace(effective, i, { ...m, fmt: (e.target.value || undefined) as ScheduleMeasure['fmt'] }))
                    }
                  >
                    <option value="">Auto format</option>
                    {FORMATS.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="gt-btn"
                    title={`Remove ${prettyField(m.field)}`}
                    onClick={() => setMeasures(effective.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>

                  {/*
                    * The class is a GUESS — a name list plus a sample of the
                    * values, and an unrecognised field defaults to rate. `Auto`
                    * resolves through it at stamp time, so a wrong guess is a
                    * wrong formula. It is shown, and correctable.
                    */}
                  <label className="gt-class-row" title={whyClass(m.field, cls, overridden)}>
                    <span>Treat as</span>
                    <select
                      className="gt-select"
                      value={cls}
                      onChange={(e) => store.setClassOverride(source, m.field, e.target.value as MeasureClass)}
                    >
                      {(['plan', 'total', 'rate', 'text', 'nested'] as MeasureClass[]).map((c) => (
                        <option key={c} value={c}>
                          {CLASS_LABELS[c]}
                          {c === inferred ? ' — inferred' : ''}
                        </option>
                      ))}
                    </select>
                    {overridden ? (
                      <button className="gt-btn" onClick={() => store.setClassOverride(source, m.field, null)}>
                        Reset
                      </button>
                    ) : null}
                  </label>

                  {m.agg === 'sum' && cls === 'rate' ? (
                    <span className="gt-chip is-warn">
                      ⚠ {prettyField(m.field)} is a per-unit rate — summing it has no meaning
                    </span>
                  ) : null}
                </div>
              );
            })}
            {!pivotBy ? (
              <button
                className="gt-btn"
                onClick={() => {
                  const used = new Set(effective.map((m) => m.field));
                  const next = measurable.find((f) => !used.has(f)) ?? measurable[0];
                  if (next) setMeasures([...effective, { field: next, agg: 'auto' }]);
                }}
              >
                + Column
              </button>
            ) : null}
          </div>

          {usages.length > 0 ? (
            <div className="gt-field">
              <label>Usage</label>
              <SearchSelect
                value={usage}
                onChange={setUsage}
                options={[{ value: '', label: 'All usages' }, ...usages.map((u) => ({ value: u, label: u }))]}
              />
            </div>
          ) : null}

          <div className="gt-field">
            <label htmlFor="sc-filter">More filters</label>
            <input
              id="sc-filter"
              className="gt-input"
              placeholder="levels>3, name~tower"
              value={more}
              onChange={(e) => setMore(e.target.value)}
            />
          </div>

          <div className="gt-measure-row">
            <div className="gt-field">
              <label htmlFor="sc-nrows">Rows to stamp</label>
              <input
                id="sc-nrows"
                className="gt-input"
                type="number"
                min={1}
                max={500}
                value={rows}
                onChange={(e) => setRows(clampInt(e.target.value, 1, 500))}
              />
            </div>
            {pivotBy ? (
              <div className="gt-field">
                <label htmlFor="sc-ncols">Columns to stamp</label>
                <input
                  id="sc-ncols"
                  className="gt-input"
                  type="number"
                  min={1}
                  max={40}
                  value={cols}
                  onChange={(e) => setCols(clampInt(e.target.value, 1, 40))}
                />
              </div>
            ) : null}
          </div>
          <span className="gt-note">
            Rows past the last group come out blank, so leave room — they fill in as the project
            gains usages. Nothing is reserved: they are just cells.
          </span>

          <label className="gt-check">
            <input type="checkbox" checked={showTotal} onChange={(e) => setShowTotal(e.target.checked)} />
            Total row
          </label>
        </div>
      </div>
    </aside>
  );
}

function defaultMeasures(measurable: readonly string[]): ScheduleMeasure[] {
  const preferred = ['grossArea', 'area', 'dwellingTotalCount'].filter((f) => measurable.includes(f));
  const chosen = preferred.length > 0 ? preferred : measurable.slice(0, 3);
  return chosen.map((field) => ({ field, agg: 'auto' as const }));
}

function replace(list: ScheduleMeasure[], index: number, value: ScheduleMeasure): ScheduleMeasure[] {
  const next = [...list];
  next[index] = value;
  return next;
}

function clampInt(raw: string, lo: number, hi: number): number {
  const n = Number(raw) || lo;
  return Math.max(lo, Math.min(hi, Math.round(n)));
}
